import { ICellRendererParams } from '@ag-grid-community/core';
import { Component, EventEmitter, HostBinding, OnDestroy } from '@angular/core';
import { GridButton, GridOptions, GridOverflowButton, GridRow, GridRowButton, GridRowGroupButton, GridSpecialFunctions } from '../../grid.types';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { GridModalService } from '../../modals/grid-modal.service';
import { GridService } from '../../grid.service';
import { PopupService } from '@app/core/services/popup.service';
import { GridComponent } from '../../grid.component';
import { groupGridButtonsByOverflow, sortGridButtonsByOrderBy } from '../../helpers/grid-button.helpers';
import { Subscription } from 'rxjs';
import { DataTaskService } from '@app/core/services/data-task.service';

type GridFunctionRowButton = GridButton | GridRowButton;
type GridFunctionRowButtonItem = GridFunctionRowButton | GridOverflowButton<GridFunctionRowButton>;

@Component({
    selector: 'tt-grid-functions-cell-renderer',
    templateUrl: './grid-functions-cell-renderer.component.html',
    styleUrls: ['./grid-functions-cell-renderer.component.css'],
    host: {
        '[style.padding-left]': 'level',
    },
})
export class GridFunctionsCellRendererComponent implements ICellRendererAngularComp, OnDestroy {
    /**
     * List of buttons to show in the grid function column.
     */
    public buttons: GridFunctionRowButtonItem[] = [];

    public groupButtons: (GridRowGroupButton | GridOverflowButton<GridRowGroupButton>)[] = [];

    /**
     * Whether the colschema contains item path.
     */
    public hasItemPath = false;

    /**
     * Whether special func edit is true.
     */
    public hasSpecialFuncEdit = false;

    /**
     * The cell parameters for this cell.
     */
    public params?: GridFunctionsCellRendererComponentParams;

    // @HostBinding('style.paddingLeft')
    public get level() {
        return 'calc(1rem * ' + (this.params?.node?.level ?? 0) + ')';
    }

    private refreshToolbarSubscription?: Subscription;
    private readonly onCellEditingStopped = () => this.refreshButtonDisabledStates();

    public get showDefaultRowButtons(): boolean {
        return !this.params?.node?.group && ((!!this.params?.data && !this.hasItemPath && !!this.params?.data['item_path']) || this.hasItemPath || this.hasSpecialFuncEdit || this.params?.specialFunc?.duplicate || this.buttons.length > 0);
    }

    public get showGroupNodeButtons(): boolean {
        return this.params?.node?.group === true && this.groupButtons.length > 0;
    }

    public get showStandaloneGotoButton(): boolean {
        return this.hasValidGotoParameters() && !this.shouldMoveGotoButtonsIntoOverflow();
    }

    public get showStandaloneNewTabButton(): boolean {
        return this.params?.specialFunc?.newTab === true && this.showStandaloneGotoButton;
    }

    constructor(
        private popup: PopupService,
        private gridService: GridService,
        private gridModalService: GridModalService,
        private datatask: DataTaskService,
    ) {}

    public duplicateRow(event?: MouseEvent) {
        event?.preventDefault();

        if (!!this.params?.node.data) {
            this.params?.grid.duplicateRows({ nodes: [this.params.node] });
        }
    }

    /**
     * Opens row edit modal.
     */
    public async editRow(event?: MouseEvent) {
        event?.preventDefault();

        await this.gridModalService.openRowEditModal(this.params?.options, this.params?.node.data, this.params!.api);
    }

    public hasValidGotoParameters() {
        return !!this.params && this.hasItemPath && ((!!this.params.data['item_state'] && !!this.params?.data['item_parms']) || !!this.params?.data['item_path']);
    }

    public getButtonKind(button: GridButton | GridRowGroupButton | GridOverflowButton<GridButton> | GridOverflowButton<GridRowGroupButton>): string {
        const kind = button.type === 'overflow' ? button.btn_type || 'tt-button--primary im-grid-col-btn-xs pull-right' : button.btn_type || button.type || 'primary';

        return `tt-button--${kind} im-grid-col-btn-xs pull-right`;
    }

    public getButtonText(button: GridButton): string {
        return button.text || button.tooltip || '';
    }

    public async onRowButtonClick(button: GridFunctionRowButtonItem, event: MouseEvent) {
        if (button.type === 'overflow' || !('func' in button) || !(button.func instanceof Function)) return;

        event.preventDefault();

        if (!!button.save_selected_rows && [1, '1', true, 'true'].includes(button.save_selected_rows)) {
            await this.saveSelectedRows();
        }

        await button.func(this.params?.data, button, event);
    }

    public async onRowGroupButtonClick(button: GridRowGroupButton | GridOverflowButton<GridRowGroupButton>, event: MouseEvent) {
        if (button.type === 'overflow' || !('func' in button) || !(button.func instanceof Function)) return;

        event.preventDefault();

        await button.func(this.params!.node, button, event);
    }

    agInit(params: GridFunctionsCellRendererComponentParams): void {
        this.params = params;
        this.hasItemPath = this.params.options?.data?.hasGotoParms === true;
        this.hasSpecialFuncEdit = this.params.options?.data?.hasSpecialFuncEdit === true;

        const buttons = sortGridButtonsByOrderBy([...(params.specialFunc?.buttons?.filter((button) => !button.type || (button.type !== 'noclick' && button.type !== 'cell')).map((button) => ({ ...button })) || [])], { dangerLastForUnordered: true });

        this.buttons = groupGridButtonsByOverflow(buttons, { renderSyntheticOverflowAtStart: true, syntheticOverflowTooltipOnly: true });
        this.moveGotoButtonsIntoOverflowIfNeeded();

        if (params.node.group === true) {
            this.groupButtons = groupGridButtonsByOverflow(sortGridButtonsByOrderBy((this.params.specialFunc?.groupButtons?.filter((button) => button.field === params.node.field) ?? []).map((button) => ({ ...button }))), { renderSyntheticOverflowAtStart: true, syntheticOverflowTooltipOnly: true });
        }

        this.setInitialButtonDisabledStates();
        this.refreshButtonDisabledStates();

        this.refreshToolbarSubscription = params.grid.refreshToolbar.subscribe({
            next: () => this.refreshButtonDisabledStates(),
        });

        params.api.addEventListener('cellEditingStopped', this.onCellEditingStopped);
    }

    refresh(_: ICellRendererParams<any, any, any>): boolean {
        return false;
    }

    ngOnDestroy(): void {
        this.refreshToolbarSubscription?.unsubscribe();
        this.params?.api.removeEventListener('cellEditingStopped', this.onCellEditingStopped);
        // this.popup.closeAllPopups();
    }

    private refreshButtonDisabledStates() {
        this.refreshButtonsDisabledState(this.getFlatButtons(this.buttons));
        this.refreshButtonsDisabledState(this.getFlatButtons(this.groupButtons));
    }

    private moveGotoButtonsIntoOverflowIfNeeded(): void {
        if (!this.shouldMoveGotoButtonsIntoOverflow()) return;

        const overflowButtons = this.getRowOverflowButtons();
        const overflowButton = overflowButtons.find((button) => button.name === 'default') ?? overflowButtons[0];

        if (!overflowButton) return;

        const existingButtonNames = new Set(overflowButton.buttons.map((button) => button.name));
        const gotoButtons = this.getBuiltInGotoOverflowButtons().filter((button) => !existingButtonNames.has(button.name));

        overflowButton.buttons = [...gotoButtons, ...overflowButton.buttons];
    }

    private shouldMoveGotoButtonsIntoOverflow(): boolean {
        return this.params?.specialFunc?.gotoInOverflow === true && this.hasItemStateGotoParameters() && this.getRowOverflowButtons().length > 0;
    }

    private getRowOverflowButtons(): GridOverflowButton<GridFunctionRowButton>[] {
        return this.buttons.filter((button): button is GridOverflowButton<GridFunctionRowButton> => button.type === 'overflow' && 'buttons' in button);
    }

    private hasItemStateGotoParameters(): boolean {
        return !!this.params?.data?.['item_state'];
    }

    private getBuiltInGotoOverflowButtons(): GridFunctionRowButton[] {
        const itemState = this.params?.data?.['item_state'];
        const tooltip = `goto_${itemState}`;
        const buttons: GridFunctionRowButton[] = [
            {
                name: 'tt_builtin_staticgoto',
                type: 'staticgoto',
                icon: 'fas fa-chevron-right',
                tooltip,
                translate: true,
                translateTooltip: true,
            },
        ];

        if (this.params?.specialFunc?.newTab === true) {
            buttons.push({
                name: 'tt_builtin_staticgoto_new_tab',
                type: 'staticgoto',
                icon: 'fas fa-window-restore',
                tooltip,
                translate: true,
                translateTooltip: true,
                _forceNewTab: true,
            });
        }

        return buttons;
    }

    private setInitialButtonDisabledStates() {
        for (const button of [...this.getFlatButtons(this.buttons), ...this.getFlatButtons(this.groupButtons)]) {
            button._disabled = this.getInitialDisabledValueForButton(button);
        }

        this.refreshOverflowButtonDisabledStates();
    }

    private async refreshButtonsDisabledState<TButton extends GridButton | GridRowGroupButton>(buttons: TButton[]) {
        for (const button of buttons) {
            button._disabled = await this.getDisabledValueForButton(button);
        }

        this.refreshOverflowButtonDisabledStates();
    }

    private refreshOverflowButtonDisabledStates() {
        for (const button of [...this.buttons, ...this.groupButtons]) {
            if (button.type === 'overflow' && 'buttons' in button) {
                const overflowButtons = button.buttons as (GridButton | GridRowGroupButton)[];

                button._disabled = overflowButtons.every((item) => item._disabled === 'hidden') ? 'hidden' : false;
                button.buttons = [...button.buttons];
            }
        }
    }

    private getFlatButtons<TButton extends GridButton | GridRowGroupButton>(buttons: (TButton | GridOverflowButton<TButton>)[]): TButton[] {
        return buttons.flatMap((button) => (button.type === 'overflow' && 'buttons' in button ? button.buttons : [button as TButton]));
    }

    private getInitialDisabledValueForButton(button: GridButton | GridRowGroupButton): boolean | 'hidden' {
        const disabled = (button as GridRowButton | GridRowGroupButton).disabled as unknown;

        if (disabled === undefined || disabled === null || disabled === false) return false;
        if (disabled === true) return true;
        if (disabled === 'hidden') return 'hidden';

        return 'hidden';
    }

    private async getDisabledValueForButton(button: GridButton | GridRowGroupButton): Promise<boolean | 'hidden'> {
        if (!this.params) return false;

        const disabled = (button as GridRowButton | GridRowGroupButton).disabled as unknown;

        if (typeof disabled === 'boolean') return disabled;
        if (disabled === 'hidden') return 'hidden';

        if (typeof disabled === 'string' && this.params.data && !this.params.node.isRowPinned()) {
            let disabledCondition = disabled;

            for (const [key, value] of Object.entries(this.params.data)) {
                if (typeof value === 'string') {
                    disabledCondition = disabledCondition.replaceAll(key + ' ', `"${value}" `);
                } else {
                    disabledCondition = disabledCondition.replaceAll(key + ' ', `${value} `);
                }
            }

            try {
                return Function('return ' + disabledCondition)();
            } catch (error) {
                console.error(error);
                return false;
            }
        }

        if (disabled instanceof Function) {
            try {
                const result = disabled(this.params.data, { node: this.params.node });

                return result instanceof Promise ? await result : result;
            } catch (error) {
                console.error(error);
                return false;
            }
        }

        return false;
    }

    private async saveSelectedRows() {
        if (!this.params?.options.dataTask?.loadData?.method) {
            console.error('Remember id on grid needs to be set in order to save the selected rows');
        } else {
            let encodedSelectedRows = '';

            try {
                encodedSelectedRows = this.gridService.encodeIsSelectedRows(this.params.options);
            } catch (error) {
                console.error(error);
            }

            await this.datatask.Post(616, { variablename: 'dtk_' + this.params.options.dataTask.loadData.method + '.selected_rows', variablevalue: encodedSelectedRows });
        }
    }
}

export type GridFunctionsCellRendererComponentParams<TType extends GridRow = GridRow> = ICellRendererParams & { specialFunc?: GridSpecialFunctions; grid: GridComponent<TType>; options: GridOptions<TType>; openModalEventEmitter: EventEmitter<ModalEvent>; navigateEventEmitter: EventEmitter<NavigateEvent>; datataskEventEmitter: EventEmitter<DatataskEvent> };

/**
 * Represents navigation event from special func buttons.
 */
export interface NavigateEvent extends CustomEvent {
    rowData: GridRow | null;
    newTab: boolean;
    itemState?: string;
    itemParms?: string | { [key: string]: string };
}

/**
 * Represents open-modal event from special func buttons.
 */
export interface ModalEvent extends CustomEvent {
    modalComponent: string;
    modalSize: string;

    /**
     *
     */
    rowData: GridRow | null;
}

/**
 * Represents a datatask event from special func buttons.
 */
export interface DatataskEvent extends CustomEvent {
    /**
     * The data of the row on which the button was pressed.
     */
    rowData: GridRow | null;

    /**
     * The datatask keyno to run.
     */
    p2_datatask_keyno: number;

    /**
     * If the keyno is `2837` (goto), should the navigation happen in a new tab.
     */
    newTab: boolean;
}

/**
 * Represents a custom
 */
export interface CustomEvent {
    preventDefault: () => void;
    defaultPrevented: boolean;
}
